import "./products.css";
import "../services/service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";

/* ─── SVG icon helper (same as Unibuilder) ───────────────────────────────── */
const Icon = ({ d, d2, d3, circle, rect, poly, line, ...rest }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {circle &&
      (Array.isArray(circle) ? (
        circle.map((c, i) => <circle key={i} {...c} />)
      ) : (
        <circle {...circle} />
      ))}
    {rect &&
      (Array.isArray(rect) ? (
        rect.map((r, i) => <rect key={i} {...r} />)
      ) : (
        <rect {...rect} />
      ))}
    {poly &&
      (Array.isArray(poly) ? (
        poly.map((p, i) => <polyline key={i} points={p} />)
      ) : (
        <polyline points={poly} />
      ))}
    {line &&
      (Array.isArray(line) ? (
        line.map((l, i) => <line key={i} {...l} />)
      ) : (
        <line {...line} />
      ))}
    {d && <path d={d} />}
    {d2 && <path d={d2} />}
    {d3 && <path d={d3} />}
  </svg>
);

/* ─── Bento feature cells ─────────────────────────────────────────────────── */
const bentoFeatures = [
  {
    size: "wide",
    accent: true,
    icon: {
      poly: ["9 11 12 14 22 4"],
      d: "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
    },
    title: "Task Management System",
    desc: "Create, organize, and manage daily tasks efficiently using a simple and structured interface.",
    tags: [
      "Task Creation",
      "Status Tracking",
      "Task Prioritization",
      "Organized Task Lists",
    ],
  },
  {
    size: "med",
    icon: {
      d: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9",
      d2: "M13.73 21a2 2 0 0 1-3.46 0",
    },
    title: "Smart Reminder & Alerts",
    desc: "Set reminders and notifications to ensure important tasks and activities are never missed.",
  },
  {
    size: "third",
    icon: {
      d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
      circle: { cx: "9", cy: "7", r: "4" },
      d2: "M23 21v-2a4 4 0 0 0-3-3.87",
      d3: "M16 3.13a4 4 0 0 1 0 7.75",
    },
    title: "Team Collaboration",
    desc: "Collaborate with teams and manage shared operational activities from a centralized workspace.",
  },
  {
    size: "third",
    icon: {
      rect: { x: "3", y: "4", width: "18", height: "18", rx: "2" },
      line: [
        { x1: "16", y1: "2", x2: "16", y2: "6" },
        { x1: "8", y1: "2", x2: "8", y2: "6" },
        { x1: "3", y1: "10", x2: "21", y2: "10" },
      ],
    },
    title: "Daily Planning & Scheduling",
    desc: "Plan daily operations, schedules, and activities with better organization and visibility.",
  },
  {
    size: "third",
    icon: {
      line: [
        { x1: "18", y1: "20", x2: "18", y2: "10" },
        { x1: "12", y1: "20", x2: "12", y2: "4" },
        { x1: "6", y1: "20", x2: "6", y2: "14" },
      ],
    },
    title: "Progress Tracking & Monitoring",
    desc: "Track task completion, monitor operational progress, and improve productivity.",
  },
];

/* ─── How it works steps ─────────────────────────────────────────────────── */
const steps = [
  {
    num: "01",
    title: "Create Your Task",
    desc: "Type what you need to do in plain language. TaskAlarm parses it and fills in the details — title, due date, and priority — automatically.",
  },
  {
    num: "02",
    title: "Set Your Reminders",
    desc: "Choose when and how you want to be reminded. Set multiple alerts per task and pick delivery channels — push, email, or SMS.",
  },
  {
    num: "03",
    title: "Let AI Optimise",
    desc: "The AI engine analyses your workload and deadlines, suggests the best time to tackle each task, and warns you before schedules get tight.",
  },
  {
    num: "04",
    title: "Complete & Track",
    desc: "Mark tasks done, review productivity reports, and let TaskAlarm learn your patterns to get smarter with every cycle.",
  },
];

/* ─── Use cases ──────────────────────────────────────────────────────────── */
const useCases = [
  {
    icon: {
      poly: ["9 11 12 14 22 4"],
      d: "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
    },
    title: "Daily Task Management",
    desc: "Manage and track daily tasks and operational activities efficiently from a centralized platform.",
    bullets: [
      "Daily task management",
      "Workflow tracking",
      "Reminder management",
    ],
  },
  {
    icon: {
      d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
      circle: { cx: "9", cy: "7", r: "4" },
      d2: "M23 21v-2a4 4 0 0 0-3-3.87",
      d3: "M16 3.13a4 4 0 0 1 0 7.75",
    },
    title: "Team Coordination",
    desc: "Coordinate teams and manage shared operational workflows with full visibility across activities.",
    bullets: [
      "Team coordination",
      "Collaborative workflows",
      "Employee activity tracking",
    ],
  },
  {
    icon: {
      d: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z",
      d2: "M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16",
    },
    title: "Business Operations",
    desc: "Streamline business operations and manage operational scheduling with structured task management.",
    bullets: [
      "Business operations",
      "Operational scheduling",
      "Activity management",
    ],
  },
  {
    icon: { poly: ["13 2 3 14 12 14 11 22 21 10 12 10 13 2"] },
    title: "Productivity Management",
    desc: "Track productivity, improve operational efficiency, and streamline daily performance management.",
    bullets: [
      "Productivity management",
      "Progress tracking",
      "Performance monitoring",
    ],
  },
];

/* ─── Capabilities ────────────────────────────────────────────────────────── */
const capabilities = [
  {
    icon: {
      d: "M12 2L2 7l10 5 10-5-10-5z",
      d2: "M2 17l10 5 10-5",
      d3: "M2 12l10 5 10-5",
    },
    label: "Better Task Organization",
    sub: "Centralized task handling, structured workflows and organized daily operations",
  },
  {
    icon: {
      poly: ["23 6 13.5 15.5 8.5 10.5 1 18"],
      d: "M17 6h6v6",
    },
    label: "Improved Productivity",
    sub: "Faster task execution, reduced missed activities and better time management",
  },
  {
    icon: {
      d: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9",
      d2: "M13.73 21a2 2 0 0 1-3.46 0",
    },
    label: "Smart Reminder System",
    sub: "Automated reminders, deadline notifications and timely activity alerts",
  },
  {
    icon: {
      circle: [{ cx: "12", cy: "12", r: "3" }],
      d: "M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83",
    },
    label: "Simplified Daily Operations",
    sub: "Manage tasks, reminders and schedules from one centralized platform",
  },
];

/* ─── Stats ───────────────────────────────────────────────────────────────── */
const stats = [
  { value: "3×", label: "Productivity Boost" },
  { value: "98%", label: "On-Time Task Rate" },
  { value: "< 30s", label: "Task Creation Time" },
  { value: "5 apps", label: "Calendar Integrations" },
];

/* ─── Mockup bar heights for chart ───────────────────────────────────────── */
const chartBars = [30, 55, 45, 70, 60, 90, 75];

/* ════════════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════════════ */
export default function TaskAlarm() {
  return (
    <>
      <SEO
        title="Task Alarm: Smart Reminder & Productivity App | Best Code Technology"
        description="Task Alarm is a smart reminder app to manage tasks and improve productivity."
        keywords="task alarm app, reminder app, productivity tool"
        url="https://bestcodetechnology.in/products/task-alarm"
      />

      <div className="uni-page">
        {/* ── HERO ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <h1 className="svc-hero__headline">
                  Manage Tasks, <span>Reminders &amp; Daily Operations</span>
                </h1>
                <p className="svc-hero__desc">
                  Task Alarm is a smart task management and reminder platform
                  designed to help individuals and teams organize tasks, track
                  activities, manage schedules, and improve daily productivity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── BENTO FEATURES ── */}
        <section className="uni-bento">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Key Features</span>
                <h2 className="svc-section-title mt-3">
                  Core Features of Task Alarm
                </h2>
                <p className="svc-section-sub">
                  Task Alarm helps users manage work, reminders, schedules, and
                  operational activities from a centralized platform.
                </p>
              </div>
            </div>

            <div className="uni-bento__grid">
              {bentoFeatures.map((f, i) => (
                <div
                  key={i}
                  className={`uni-bento__cell uni-bento__cell--${f.size}${f.accent ? " uni-bento__cell--accent" : ""}`}
                >
                  <div className="uni-bento__cell-icon">
                    <Icon {...f.icon} />
                  </div>
                  <h3 className="uni-bento__cell-title">{f.title}</h3>
                  <p className="uni-bento__cell-desc">{f.desc}</p>
                  {f.tags && (
                    <div className="uni-bento__tags">
                      {f.tags.map((t) => (
                        <span key={t} className="uni-bento__tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="uni-steps">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">How It Works</span>
                <h2 className="svc-section-title mt-3">
                  From Task Idea to Done in Four Steps
                </h2>
                <p className="svc-section-sub">
                  No configuration overhead, no complex onboarding. Task Alarm
                  gets you organised and alerted from the moment you sign in.
                </p>
              </div>
            </div>

            <div className="row g-4 uni-steps__row">
              {steps.map((s) => (
                <div key={s.num} className="col-12 col-sm-6 col-lg-3">
                  <div className="uni-step">
                    <div className="uni-step__num">{s.num}</div>
                    <h4 className="uni-step__title">{s.title}</h4>
                    <p className="uni-step__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── USE CASES ── */}
        <section className="uni-cases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Use Cases</span>
                <h2 className="svc-section-title mt-3">Task Alarm Use Cases</h2>
                <p className="svc-section-sub">
                  Task Alarm can be used across multiple business and
                  operational environments.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {useCases.map((uc, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-3">
                  <div className="uni-case-card">
                    <div className="uni-case-card__icon">
                      <Icon {...uc.icon} />
                    </div>
                    <h4 className="uni-case-card__title">{uc.title}</h4>
                    <p className="uni-case-card__desc">{uc.desc}</p>
                    <ul className="uni-case-card__list">
                      {uc.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CAPABILITIES ── */}
        <section className="uni-capabilities">
          <div className="container-xl">
            <div className="row align-items-center g-5">
              {/* left copy */}
              <div className="col-12 col-lg-5">
                <span className="pill-label">Advanced Capabilities</span>
                <h2 className="svc-section-title mt-3">
                  Why Businesses Use Task Alarm
                </h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Task Alarm helps businesses and teams simplify operational
                  task management and improve productivity.
                </p>
              </div>

              {/* right visual panel */}
              <div className="col-12 col-lg-7">
                <div className="uni-cap-visual">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="uni-cap-row">
                      <div className="uni-cap-row__icon">
                        <Icon {...cap.icon} />
                      </div>
                      <div>
                        <p className="uni-cap-row__label">{cap.label}</p>
                        <p className="uni-cap-row__sub">{cap.sub}</p>
                      </div>
                      <span className="uni-cap-row__badge">Included</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
